<script setup lang="ts">
import {type UserInterface, useUserStore} from "@/stores/user.ts";
import {computed, type ComputedRef, defineAsyncComponent, onMounted, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import AdminMenuComponent from "@/components/admin/AdminMenuComponent.vue";

const ContactFormComponent = defineAsyncComponent(() => import('@/components/contactFormComponent.vue'))
const LocationMapComponent = defineAsyncComponent(() => import('@/components/locationMapComponent.vue'))

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const auth: ComputedRef<UserInterface | null> = computed(() => userStore.auth);
const isAdminRoute = computed(() => route.matched.some(r => r.meta.isAdmin === true));
const cr=computed(()=>router.currentRoute.value.path);
const contact_us=ref(false);
const show_location=ref(false);

const appReady = ref(false);
onMounted(async () => {
    await router.isReady();
    appReady.value = true;
});
</script>

<template>
    <template v-if="appReady">
        <AdminMenuComponent v-if="isAdminRoute && auth" :auth="auth" />
        <header v-else class="app-header">
            <button class="icon-btn" @click="show_location = true" aria-label="Location">
                <i class="fi fi-rr-marker"></i>
            </button>

            <img src="/new-logo.png" alt="Logo" width="135" height="24" class="logo" />

            <button class="icon-btn" @click="contact_us = true" aria-label="Contact">
                <i class="fi fi-rr-envelope"></i>
            </button>
        </header>
    </template>

    <main class="app-content">
        <router-view />
    </main>

    <footer class="ios-nav" v-if="!isAdminRoute">
        <button class="tab" :class="{ active: cr === '/services' }" @click="router.push('/services')">
            <i class="fi fi-sr-home"></i><span>{{ t('home') }}</span>
        </button>
        <button class="tab" :class="{ active: cr === '/reservations' }" @click="router.push('/reservations')">
            <i class="fi fi-sr-calendar"></i><span>{{ t('reservations') }}</span>
        </button>
        <button class="tab" :class="{ active: cr === '/profile' }" @click="router.push('/profile')">
            <i class="fi fi-sr-user"></i><span>{{ t('profile') }}</span>
        </button>
        <button v-if="auth?.is_admin" class="tab" :class="{ active: cr.startsWith('/administration') }"
                @click="router.push('/administration/reservations')">
            <i class="fi fi-sr-settings"></i><span>{{ t('admin') }}</span>
        </button>
    </footer>

    <contact-form-component v-if="contact_us" v-model:contact_us="contact_us" :auth="auth"></contact-form-component>
    <location-map-component v-if="show_location" v-model:show_location="show_location" :auth="auth" ></location-map-component>
</template>

<style scoped lang="scss">
@use "sass:color";

$primary: #ffab34;
$text: #0f172a;
$text-dim: #6b7280;
$app-header-h: 56px;
$app-header-padding: 86px;
$app-tabbar-h: 65px;
$app-tabbar-padding: 80px;

html, body, #app { height: 100%; }

.app-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: $app-header-h;
  display: grid;
  grid-template-columns: 56px 1fr 56px;
  align-items: center;
  padding: 0 8px;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  border-bottom: 0.5px solid rgba(0,0,0,.08);
  z-index: 1000;

  .logo { display: block; margin: 0 auto; }

  .icon-btn {
    all: unset;
    width: 40px; height: 40px;
    display: grid; place-items: center;
    border-radius: 12px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    i { font-size: 20px; color: $text; }
  }
}

.app-content {
  height: 100vh;
  padding: $app-header-padding 12px calc($app-tabbar-padding + env(safe-area-inset-bottom, 0));
  background:url('./assets/images/bg.png');
  overflow: auto;
}

.ios-nav {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  height: $app-tabbar-h;
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-top: 0.5px solid rgba(0,0,0,.08);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  align-items: center;
  z-index: 1000;
}

.ios-nav .tab {
  all: unset;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: $text-dim;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .2px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.ios-nav .tab i { font-size: 22px; line-height: 1; }
.ios-nav .tab.active { color: $primary; }
.ios-nav .tab.active i { color: $primary; }
</style>
